import React, {
  useState,
  type ReactElement,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react'
import { Input, Box, Heading, Flex, Text, Kbd, Grid } from '@chakra-ui/react'
import { Nostalgist, type Nostalgist as NostalgistType } from 'nostalgist'
import { type Channel } from 'itty-sockets'
import CryptoJS, { MD5 } from 'crypto-js'
import { Howl, Howler } from 'howler'
import { styles } from './styles'

import itemBoxImg from '../join-va/assets/itemBox.png'
import itemsImg from '../join-va/assets/items.png'

function getOrdinalMath(n) {
  const j = n % 10
  const k = n % 100

  if (j === 1 && k !== 11) {
    return n + 'st'
  }
  if (j === 2 && k !== 12) {
    return n + 'nd'
  }
  if (j === 3 && k !== 13) {
    return n + 'rd'
  }
  return n + 'th'
}

type VaChannel = Channel & {
  myId?: string
  myAlias?: string
}

type Attack = {
  uid: string
  name: string
  attacker: string
  frames: number
}

const ITEMS = [
  'star',
  'banana',
  'greenShell',
  'redShell',
  'lightning',
  'mushroom'
]

let searchCounter = 0
let tickCount = 0
let controllerInput = '00000000'
let coins = 0
let attackQueue: Attack[] = []
let itemEffectFrame = 0
let itemEffect = -1
let attackFrames = -1
let currentAttack: Attack | undefined
let alive = true
let userPosition = -1

let selectCooldown = 0

const SOUNDS: Record<string, Howl | null> = {
  boost: null,
  fire: null,
  itemBox: null,
  lightning: null,
  shell: null,
  shellbounce: null,
  spin: null
}

const loadSounds = () => {
  for (const sound of Object.keys(SOUNDS)) {
    SOUNDS[sound] = new Howl({
      src: [`/assets/audio/video-armageddon/${sound}.ogg`],
      volume: 1,
      loop: false,
      rate: 1,
      autoplay: false
    })
  }
}

function searchUint8Array(array: number[], sequence: string[]): number[] {
  const decimalSequence = sequence.map((hex: any) => parseInt(hex, 16))
  const indices: number[] = []

  for (let i = 0; i < array.length - decimalSequence.length + 1; i++) {
    let match = true
    for (let j = 0; j < decimalSequence.length; j++) {
      if (array[i + j] !== decimalSequence[j]) {
        match = false
        break
      }
    }
    if (match) {
      indices.push(i)
    }
  }
  console.log('indices', indices)
  return indices // Return array of indices where the sequence is found
}

// function hex2bin(hex: string): string {
//   return parseInt(hex, 16).toString(2).padStart(8, '0')
// }

function dec2bin(dec: number): string {
  return dec.toString(2).padStart(8, '0')
}

enum GameState {
  OFF,
  SETUP,
  WAITING,
  START_RACE,
  RACING,
  ROULETTE,
  HAS_ITEM,
  END
}

type VideoArmageddonProps = {
  channel: VaChannel
}

const VideoArmageddon: React.FC<VideoArmageddonProps> = ({
  channel
}: VideoArmageddonProps) => {
  const emuRef = useRef<any | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const memoffsetRef = useRef<number>(0)
  const gameCanvasRef = useRef<any | null>(null)
  const moduleRef = useRef<any | null>(null)
  const prevScoreRef = useRef<number>(0)
  const gameTickInterval = useRef<any | null>(null)
  const phaseRef = useRef<GameState>(GameState.OFF)
  const [phase, setPhase] = useState<GameState>(GameState.OFF)
  const [mode, setMode] = useState<string>('classic')
  const [_frame, setFrame] = useState<number>(-1)
  const itemIndexRef = useRef<number>(-1)
  const [itemBoxFrame, setItemBoxFrame] = useState<number>(-1)
  const [attackBoxFrame, setAttackBoxFrame] = useState<number>(-1)
  const [standings, setStandings] = useState<string[]>([])

  const memGet = (address: number): number => {
    return moduleRef.current.HEAPU8.at(memoffsetRef.current + address)
  }

  const ramDump = useCallback((): number => {
    return moduleRef.current.HEAPU8.slice(
      memoffsetRef.current,
      memoffsetRef.current + 0x800
    )
  }, [])
  const memSet = useCallback(
    (address: number, value: number): void => {
      const heap = moduleRef.current.HEAPU8
      heap[memoffsetRef.current + address] = value
      moduleRef.current.HEAPU8.set(heap)
    },
    [moduleRef.current]
  )

  const testGame = (file: File): void => {
    const reader = new FileReader()
    reader.onload = function (event) {
      if (event.target != null) {
        const arrayBuffer = event.target.result as ArrayBuffer
        const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer)
        const hash = MD5(wordArray).toString()
        console.log('File hash:', hash)

        // Compare the hash with the expected hash
        const expectedHash = '86d1982fea7342c0af9679ddf3869d8d'
        if (hash.toLowerCase() === expectedHash.toLowerCase()) {
          // Load the game if the hash matches
          console.log('Loading game...')

          loadGame(file)
        } else {
          console.log('File hash does not match. Aborting...')
        }
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const loadState = async (): Promise<void> => {
    const saveBlob = await fetch(BLOB_URL)
    const state = await saveBlob.blob()
    await emuRef.current.loadState(state)
  }

  const loadGame = (file: any): void => {
    setPhase(GameState.SETUP)
    Nostalgist.launch({
      rom: file,
      core: 'quicknes',
      element: gameCanvasRef.current,
      style: {
        // width: '100%',
        height: '100%'
      },
      retroarchConfig: {
        // input_player2_down: 'num3',
        // input_player2_left: 'num2',
        // input_player2_right: 'num4',
        // input_player2_up: 'num1',
        input_player3_a: 'leftbracket',
        input_player3_b: 'rightbracket',
        input_toggle_slowmotion: 'leftbracket',
        // input_toggle_slow_motion: 'leftbracket',
        input_toggle_fast_forward: 'rightbracket',
        fastforward_ratio: 2,
        video_scale_integer: true,
        video_vsync: false,
        video_shader_enable: false
      }
    })
      .then((emu) => {
        emuRef.current = emu
        moduleRef.current = emu.getEmscriptenModule()
        clearInterval(gameTickInterval.current)
        gameTickInterval.current = setInterval(() => {
          let tester = []
          tester = searchUint8Array(moduleRef.current.HEAPU8, [
            '78',
            '7A',
            '20',
            '21',
            '04',
            '7F',
            '19'
          ])
          if (tester.length > 0) {
            console.log('found', tester)
            memoffsetRef.current = tester[0] - 0x719

            setPhase(GameState.WAITING)
            clearInterval(gameTickInterval.current)
            if (channel != null) {
              channel.send({
                id: channel.myID,
                action: 'ready',
                clientId: channel.myAlias
              })
              phaseRef.current = GameState.WAITING
            }
          } else {
            if (tester.length === 0 && searchCounter++ > 60 * 2) {
              console.log('not found')
              setErrorMessage('Wrong Game')
              clearInterval(gameTickInterval.current)
              emuRef.current.exit()
              setPhase(GameState.OFF)
            }
          }
        }, 1000 / 4)
      })
      .catch(console.error)
  }
  const queueAttack = (item: string, attacker: string, uid: string) => {
    if (attackQueue.some((a: Attack) => a.uid === uid)) return
    attackQueue.push({
      uid,
      name: item,
      attacker,
      frames: item === 'lightning' ? 1 : 60 * 3
    })
    setFrame((prev: number) => prev + 1)
  }

  const triggerAction = (message: any): void => {
    switch (message.action) {
      case 'start':
        setPhase(GameState.START_RACE)
        setMode(message.mode)
        break
      case 'item':
        if (
          message.name === channel.myAlias ||
          (message.name === 'all' && message.attacker !== channel.myAlias)
        ) {
          queueAttack(
            message.item,
            message.attacker,
            message.uid + message.date
          )
        }
        break
      case 'get':
        if (message.name === channel.myAlias || message.name === 'all') {
          const nextIndex = ITEMS.indexOf(message.item)
          itemIndexRef.current = nextIndex
        }
        break
      case 'standings':
        console.log('setting')
        setStandings(message.players.split('|'))
        if (message.final) {
          setPhase(GameState.END)
        }
        break
      default:
    }
  }

  useEffect(() => {
    channel?.remove('message', triggerAction)
    channel?.on('message', triggerAction)
    loadSounds()
  }, [channel])

  const changePhase = async (phase: GameState) => {
    switch (phase) {
      case GameState.SETUP:
        break
      case GameState.WAITING:
        clearInterval(gameTickInterval.current)
        gameTickInterval.current = setInterval(tickWaiting, 1000 / 60)
        break
      case GameState.START_RACE:
        clearInterval(gameTickInterval.current)
        await loadState()
        // emuRef.current.pause()
        emuRef.current.resume()
        gameTickInterval.current = setInterval(tickRacing, 1000 / 60)
        setPhase(GameState.RACING)
        phaseRef.current = GameState.RACING
        break
      case GameState.ROULETTE:
        setTimeout(() => {
          setPhase(GameState.HAS_ITEM)
          phaseRef.current = GameState.HAS_ITEM
          setItemBoxFrame(itemIndexRef.current)
        }, 1500)
        break
      case GameState.HAS_ITEM:
        setItemBoxFrame(itemIndexRef.current)
        break
      default:
    }
  }

  useEffect(() => {
    changePhase(phase)
    return () => {}
  }, [phase])

  const testAttack = () => {
    queueAttack(
      'redShell',
      'host',
      new Date().getTime().toString() + Math.random()
    )
  }

  const tickWaiting = () => {
    // emuRef.current.pause()
    tickCount++
  }

  const tickRacing = () => {
    if (phaseRef.current === GameState.END) {
      if (tickCount % 15 === 0) {
        memSet(0x0016, 0x1)
        emuRef.current.pause()
        return
      }
    }
    const hasDied = memGet(0x04e4) === 0x01
    if (hasDied && alive) {
      alive = false
      channel.send({
        id: channel.myID,
        action: 'died',
        clientId: channel.myAlias,
        item: 'banana'
      })
    }

    if (attackQueue.length > 0 && currentAttack === undefined) {
      currentAttack = attackQueue.shift()
      attackFrames = currentAttack!.frames
      switch (currentAttack!.name) {
        case 'redShell':
        case 'greenShell':
        case 'banana':
          setAttackBoxFrame(
            ITEMS.findIndex((item) => item === currentAttack!.name)
          )
          SOUNDS.shell?.play()
          break
      }
    }
    if (currentAttack !== undefined && attackFrames === 0) {
      const isInvincible =
        memGet(0x552) +
          memGet(0x553) +
          memGet(0x55a) +
          memGet(0x559) +
          memGet(0x5f3) >
        0
      if (!isInvincible) {
        switch (currentAttack!.name) {
          case 'lightning':
            emuRef.current.press({ button: 'a', player: 3 })
            itemEffectFrame = 60 * 5
            itemEffect = 4
            memSet(0x0016, 0x1) // set B&W mode while slow
            itemIndexRef.current = -1
            setItemBoxFrame(-1)
            if (phase === GameState.HAS_ITEM) {
              changePhase(GameState.RACING)
            }
            // play sound lightning
            SOUNDS.lightning?.play()
            break
          case 'banana':
            const isJumping = memGet(0x00d8) != 0
            if (isJumping) {
              channel.send({
                id: channel.myID,
                action: 'throw',
                clientId: channel.myAlias,
                item: 'banana'
              })
            } else {
              memSet(0x0554, 0x50) // poof animation freeze
              SOUNDS.spin?.play()
            }
            break
          case 'redShell':
          case 'greenShell':
            let hit = true
            if (currentAttack.name === 'greenShell') {
              const isJumping = memGet(0x00d8) != 0
              if (isJumping) {
                hit = false
                channel.send({
                  id: channel.myID,
                  action: 'throw',
                  clientId: channel.myAlias,
                  item: 'greenShell'
                })
              }
            }

            if (hit) {
              const currentPowerup = memGet(0x00ed)
              if (currentPowerup > 0) {
                SOUNDS.spin?.play()

                memSet(0x04f1, 0x10) // hit animation?
                if (currentPowerup === 1) {
                  memSet(0x0551, 0x26) // shrink animation
                  memSet(0x0578, 1) // make smaller
                } else {
                  memSet(0x0554, 0x10) // poof animation
                  memSet(0x0578, 2) // make smaller
                }
                memSet(0x0552, 0x70) // iframes
              } else {
                memSet(0x0554, 0x50) // poof animation
                SOUNDS.spin?.play()
              }
            }
            break
        }
      }

      currentAttack = undefined
      setFrame((prev: number) => prev + 1)
      setAttackBoxFrame(-1)
    }

    if (tickCount % 15 === 0) {
      if (!alive) {
        if (memGet(0x04e4) !== 0x01) {
          alive = true
        }
      }
      let score: number
      score = parseInt(
        (memGet(0x0715).toString(16).padStart(2, '0') as string) +
          (memGet(0x0716).toString(16).padStart(2, '0') as string) +
          (memGet(0x0717).toString(16).padStart(2, '0') as string),
        16
      )
      if (score !== prevScoreRef.current) {
        prevScoreRef.current = score
        // console.log('score is ', score)
        if (channel != null) {
          channel.send({
            id: channel.myID,
            action: 'update',
            clientId: channel.myAlias,
            score: score * 10
          })
        }
      }
    }

    let input = 0
    if (mode === '89') input = memGet(0x17)
    if (input !== 0) {
      controllerInput = dec2bin(input)
      // console.log('controllerInput is ', controllerInput, phaseRef.current)
      if (controllerInput[2] === '1' && selectCooldown === 0) {
        switch (phaseRef.current) {
          case GameState.RACING:
            if (itemIndexRef.current === -1) {
              coins = memGet(0x7da2)
              // if (coins > 10) {
              if (coins > 0) {
                // memSet(0x7da2, coins - 10)
                channel.send({
                  id: channel.myID,
                  action: 'buy',
                  clientId: channel.myAlias
                })
                setPhase(GameState.ROULETTE)
                phaseRef.current = GameState.ROULETTE
                SOUNDS.itemBox?.play()
              }
            }
            break
          case GameState.HAS_ITEM:
            switch (itemIndexRef.current) {
              case 0: // star
                memSet(0x03f2, 0x01)
                break
              case 1: // banana
                if (currentAttack?.name.includes('Shell')) {
                  // play shell bounce sound
                  SOUNDS.shell?.stop()
                  SOUNDS.shellbounce?.play()
                  currentAttack = undefined
                  setAttackBoxFrame(-1)
                } else {
                  channel.send({
                    id: channel.myID,
                    action: 'throw',
                    clientId: channel.myAlias,
                    item: ITEMS[itemIndexRef.current]
                  })
                  SOUNDS.fire?.play()
                }
                break
              case 5: // mushroom
                // toggle fast forward
                emuRef.current.press({ button: 'b', player: 3 })
                itemEffectFrame = 60 * 3
                itemEffect = 5
                SOUNDS.boost?.play()
                break
              case -1:
                break
              default:
                channel.send({
                  id: channel.myID,
                  action: 'throw',
                  clientId: channel.myAlias,
                  item: ITEMS[itemIndexRef.current]
                })
                SOUNDS.fire?.play()
            }
            itemIndexRef.current = -1
            setItemBoxFrame(-1)
            setPhase(GameState.RACING)
            phaseRef.current = GameState.RACING
            break
          case GameState.ROULETTE:
            break
        }
      }
      if (controllerInput[2] === '1') {
        selectCooldown = 30
      }
    } else {
      controllerInput = '00000000'
    }

    if (selectCooldown > 0) {
      selectCooldown--
    }

    if (itemEffectFrame > 0) {
      itemEffectFrame--
    }
    if (attackFrames > 0) {
      attackFrames--
    }
    if (itemEffectFrame === 0 && itemEffect !== -1) {
      switch (itemEffect) {
        case 4:
          emuRef.current.press({ button: 'a', player: 3 })
          memSet(0x0016, 0x0) // set B&W mode while slow
          itemEffect = -1
          break
        case 5:
          emuRef.current.press({ button: 'b', player: 3 })
          itemEffect = -1
          break
      }
    }

    if (phaseRef.current === GameState.ROULETTE) {
      if (tickCount % 5 === 0) {
        setItemBoxFrame((prevIndex) => (prevIndex + 1) % ITEMS.length)
      }
    }

    tickCount++
  }

  const standingsModule = useMemo(() => {
    userPosition = standings.indexOf(channel.myAlias)
    let before = Math.max(0, userPosition - 6)
    let after = Math.min(standings.length - 1, userPosition + 6)
    const visibleStandings = standings.slice(before, after + 1)
    const userSubPosition = visibleStandings.indexOf(channel.myAlias)
    return (
      <Box fontSize={{ base: '32px', '2xl': '64px' }}>
        {visibleStandings.map((name, index) => {
          return (
            <Text
              key={index + name}
              fontSize={`calc(1em - ${Math.abs(userSubPosition - index) * 0.15}em)`}
              opacity={`calc(1 - ${Math.abs(userSubPosition - index) * 0.15})`}
              fontWeight="bold"
              lineHeight="1.1"
              m="0"
            >
              {1 + userPosition - (userSubPosition - index)}. {name}
            </Text>
          )
        })}
      </Box>
    )
  }, [standings])

  const itemsBoxStyle = {
    left: `calc(-${itemBoxFrame % 3} * 100%)`,
    top: itemBoxFrame > 2 ? 'unset' : 0,
    bottom: itemBoxFrame > 2 ? 0 : 'unset',
    opacity: itemBoxFrame === -1 ? 0 : 1
  }
  const attackItemsBoxStyle = {
    left: `calc(-${attackBoxFrame % 3} * 100%)`,
    top: attackBoxFrame > 2 ? 'unset' : 0,
    bottom: attackBoxFrame > 2 ? 0 : 'unset',
    opacity: attackBoxFrame === -1 ? 0 : 1
  }
  return (
    <Flex sx={{ ...styles.container, maxW: 'unset' }}>
      <Flex
        display={phase === GameState.OFF ? 'flex' : 'none'}
        zIndex={50}
        pos="absolute"
        top={0}
        left={0}
        height="100%"
        aspectRatio={4 / 3}
        justifyContent="center"
        alignItems="center"
      >
        <Box maxW="500px">
          <Heading
            fontWeight={800}
            letterSpacing={'-0.05em'}
            fontSize={{ base: '20px', md: '25px' }}
            textAlign="center"
            mb="20px"
          >
            Select your Super Mario 3 Rom
          </Heading>
          <Text
            textAlign="center"
            fontSize={{ base: '16px', '2xl': '21px' }}
            lineHeight={1.5}
          >
            The rom MD5 Checksum must be <br />
            <strong>86D1982FEA7342C0AF9679DDF3869D8D</strong>
            <br />
            otherwise the emulator will not start.
          </Text>
          <Box
            textAlign="center"
            fontSize={{ base: '16px', '2xl': '21px' }}
            lineHeight={1.5}
          >
            <Heading fontSize={{ base: '16px', '2xl': '21px' }} mb="20px">
              Keyboard Controls
            </Heading>
            <Grid
              maxW="300px"
              mx="auto"
              templateColumns="1fr 3fr"
              gap="25px"
              sx={{
                '.chakra-kbd': {
                  fontSize: '1em'
                },
                '.chakra-text': {
                  textAlign: 'left',
                  mb: '0',
                  fontSize: '1.05em',
                  lineHeight: '1.25em'
                }
              }}
            >
              <Flex
                display="inline-flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                gap="3px"
              >
                <Flex>
                  <Kbd>↑</Kbd>
                </Flex>
                <Flex gap="3px">
                  <Kbd>←</Kbd>
                  <Kbd>↓</Kbd>
                  <Kbd>→</Kbd>
                </Flex>
              </Flex>
              <Text
                sx={{
                  mb: '0',
                  lineHeight: '55px !important'
                }}
              >
                Move
              </Text>
              <Box>
                <Kbd>Z</Kbd>
              </Box>{' '}
              <Text> B (Run) </Text>
              <Box>
                <Kbd>X</Kbd>
              </Box>{' '}
              <Text> A (Jump) </Text>
              <Box>
                <Kbd>enter</Kbd>
              </Box>{' '}
              <Text>Start (Pause) </Text>
              <Box>
                <Kbd>shift</Kbd>
              </Box>{' '}
              <Text>Select (Buy / Fire Items in &quot;89&quot; Mode) </Text>
              <Box>
                <Kbd>F1</Kbd>
              </Box>{' '}
              <Text>Open RetroArch Menu </Text>
            </Grid>
          </Box>
          <Text
            textAlign="center"
            lineHeight={1.5}
            mt="20px"
            fontSize={{ base: '16px', '2xl': '21px' }}
          >
            <em>
              Controllers are supported but may need to be remapped in RetroArch
            </em>
          </Text>
          <Box>
            <Input
              type="file"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e?.target?.files != null) {
                  testGame(e.target.files[0])
                }
              }}
            />
          </Box>
        </Box>
      </Flex>
      <Box
        pos="absolute"
        top={0}
        right={0}
        bottom={0}
        width="calc(100vw - (4/3 * (100vh - 59px)))"
        height="100%"
        backgroundColor="black"
        color="white"
        p="20px"
      >
        <Box maxH="100%" overflow="auto">
          <Box
            sx={{
              '.chakra-heading, .chakra-text': {
                color: 'white'
                // maxW: '500px'
              }
            }}
          >
            <Heading
              fontSize={{ base: '28px', '2xl': '32px' }}
              textAlign="left"
            >
              Video Armageddon
            </Heading>
            <Text
              fontSize={{ base: '16', '2xl': '18px' }}
              lineHeight={1.4}
              textAlign="left"
            >
              Race to get the highest score in the set time limit. Everyone will
              start at World 1-1 and play until time runs out. In '89' Mode,
              Coins collected in game can be used to purchase &quot;Mario
              Kart&quot; style items.
            </Text>
            <Box
              maxW="650px"
              display={
                [GameState.OFF, GameState.SETUP, GameState.WAITING].includes(
                  phase
                )
                  ? 'block'
                  : 'none'
              }
            >
              <Heading
                fontSize={{ base: '20px', '2xl': '26px' }}
                textAlign="left"
              >
                Items: (Only available in &quot;89&quot; Mode)
              </Heading>
              <Text fontSize={{ base: '14px', '2xl': '21px' }} lineHeight="1.5">
                Hit Select on your controller to purchase items for 10 in-game
                coins. Hit select again to fire the item.
              </Text>
              <br />
              <Text fontSize={{ base: '14px', '2xl': '21px' }} lineHeight="1.5">
                <Box as="strong" fontSize={{ base: '18px', '2xl': '23px' }}>
                  Green Shells / Red Shells
                </Box>
                <br />
                Throw shells target the player ahead of you, removing their
                in-game power-up. (If they arealready small mario, they are
                temporaluy stunned )
              </Text>

              <Text fontSize={{ base: '14px', '2xl': '21px' }} lineHeight="1.5">
                You can dodge Green shells by jumping just as they are about to
                hit. If you do dodge the shell, it will continue to the player
                ahead of you. Red Shells cannot be dodged.
              </Text>
              <br />

              <Text fontSize={{ base: '14px', '2xl': '21px' }} lineHeight="1.5">
                <Box as="strong" fontSize={{ base: '18px', '2xl': '23px' }}>
                  Bananas
                </Box>
                <br />
                Temporarily stun players behind you. Can also be fired when a
                shell is chassing you to block them. You can jump over bananas
                the same as green shells, and they will continue to the player
                behind you.{' '}
              </Text>

              <Text fontSize={{ base: '14px', '2xl': '21px' }} lineHeight="1.5">
                Bananas and Shells cannot affect you in the Overworld or any
                other time the in-game timer is frozen. (After beating a boss,
                mushroom house, etc)
              </Text>
              <br />

              <Text fontSize={{ base: '14px', '2xl': '21px' }} lineHeight="1.5">
                <Box as="strong" fontSize={{ base: '18px', '2xl': '23px' }}>
                  Mushrooms
                </Box>
                <br />
                Get a 50% speed boost in the game (but be careful, it&apos;s not
                just Mario, the whole game is running 50% faster)
              </Text>
              <br />

              <Text
                fontSize={{ base: '16', '2xl': '18px' }}
                paddingRight="120px"
              >
                <Box as="strong" fontSize={{ base: '18px', '2xl': '23px' }}>
                  Lightning
                </Box>
                <br />
                Make all the other players lose their currently held items and
                slow them down for a short time.
              </Text>
              <br />

              <Text
                fontSize={{ base: '16', '2xl': '18px' }}
                paddingRight="120px"
              >
                <Box as="strong" fontSize={{ base: '18px', '2xl': '23px' }}>
                  Star
                </Box>
                <br />
                Give yourself an in-game Star power-up, which makes you
                invincible to in-game enemies AND player-thrown items.
              </Text>
              <br />
            </Box>
            <Flex
              maxW="650px"
              flex="1"
              display={
                [GameState.OFF, GameState.SETUP, GameState.WAITING].includes(
                  phase
                )
                  ? 'none'
                  : 'block'
              }
            >
              <Heading>Current Standings ({standings.length})</Heading>
              {standingsModule}
            </Flex>
          </Box>
        </Box>
      </Box>
      <Box
        ref={gameCanvasRef}
        id="gameCanvas"
        as="canvas"
        pos="absolute"
        top={0}
        left={0}
        height="100%"
        aspectRatio={4 / 3}
      />
      <Text
        sx={{
          display: phase === GameState.END ? 'block' : 'none',
          position: 'absolute',
          width: 'calc(100vh * (4/3))',
          top: '50%',
          left: '0',
          transform: 'translate(0, -50%)',
          textAlign: 'center',
          fontSize: '5vw',
          color: 'white',
          fontWeight: 'bold',
          textShadow: `  -0.2vw -0.2vw 0 #000,
             0.2vw -0.2vw 0 #000,
             -0.2vw  0.2vw 0 #000,
             0.2vw  0.2vw 0 #000,
             0.25vw  0.25vw 0 #000,
             0.3vw  0.3vw 0 #000,
             0.4vw  0.4vw 0 #000,
             0.5vw  0.5vw 0 #000,
             0.6vw  0.6vw 0 #000,
             0px -0.2vw 0 #000,
             0px  0.2vw 0 #000,
             -0.2vw  0px 0 #000,
             0.2vw  0px 0 #000;
             `
        }}
      >
        {getOrdinalMath(userPosition + 1)} Place
      </Text>

      <Box
        sx={{
          opacity:
            [GameState.OFF, GameState.SETUP, GameState.WAITING].includes(
              phase
            ) || mode !== '89'
              ? 0
              : 1
        }}
      >
        <Box
          as="img"
          src={itemBoxImg}
          sx={{
            w: '7vw',
            h: 'auto',
            position: 'absolute',
            left: 0,
            top: 0,
            imageRendering: 'pixelated',
            opacity: phase === GameState.ROULETTE ? 0 : 1
          }}
        />
        <Box
          sx={{
            w: '7vw',
            aspectRatio: '26/18',
            overflow: 'hidden',
            position: 'absolute',
            left: 0,
            top: 0,
            imageRendering: 'pixelated'
          }}
        >
          <Box
            as="img"
            src={itemsImg}
            sx={{ minW: '300%', position: 'absolute', ...itemsBoxStyle }}
          />
        </Box>
        <Box
          sx={{
            w: '7vw',
            aspectRatio: '26/18',
            overflow: 'hidden',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: attackBoxFrame === 1 ? 'calc(100% - 7vw)' : 0,
            imageRendering: 'pixelated'
          }}
        >
          <Box
            as="img"
            src={itemsImg}
            sx={{ minW: '300%', position: 'absolute', ...attackItemsBoxStyle }}
          />
        </Box>
      </Box>
    </Flex>
  )
}

export default VideoArmageddon

const BLOB_URL =
  'data:application/octet-stream;base64,I1JaSVB2ASMAAAIAIDIAAAAAAAB4BQAAeJztmm1sU1UYx597b9d1MHbJCNsUlW4CnVsIC1GWKEJbdrdO2axtZ8yCS4w6IgYzBT75YTUxhg+KA9a5bu6FsrVjsA2h+EJibKI4/MAyFSbf9JqQaKIyEohNtp35nHt71ze2od0YN9zf7bnnPOc5z3m5/9v347A4XRaXwFQJVcb0zQDVgtM5jbgqqwQDAPCYbrEAO+w1DmpPtEyhBWBH+3HM+/LccB5873Tvw7Z8yVZ+i6mUzy8q5UtySiFnCw+5NJl4KMnh9+Tuyb2OMRaMtWHe0viA4UrjaoMRHsp4BLg8vsht4Kd3rQdIB4aHCX0aTBcDZME/aQxMY0A2wMOQlglwdW86mu/+WoNTYmCHy7ETK4HOm57o/Kosdged+646W2Xas8serGDch0MAOx2WKsCgboABM1iYkk3gmz5uBolfcC1AO5TSSvD2e3pqek/2QwSzW8EMCZhjSvIEAF6WOuOeGpoCVrfxzGowRhoapNJsmDasy88vWGeybNu23VJWtkNHFThtNEMGdDOvw9+Qzq1dk7uKX446hGmAIW/s9j1x8SauSVofStAnX6zFY5G7v3PqftM/qd878H63+5Z8M28pmGqpPhhmGAOmcUzhJSubwBw2mW3GjWA2bgJbGAVqeIwbbSjmroYZNoBpKCb3Yxp0X2D97mF2EMt9mE5H/ZwpzHyzKaZeyaW4mFxqD1CYeV1i69PkTzJeLPDkJhmXIX/hCeb0FlvZ/QeKNzA35riR52QtEYE51d7lQY2IyHKdza1D8cqNJItJRmBY6cBegDhAxyT2jM+UlGB0d9pyzarCosLCV3wvWZkNhVCX2rCpERge03PZXHY2x+m5dSMZcm3yShr1QC/dGw1ud2z1aMg/Z/cMsByrGJtTn26030T5kuRcIAoXqd+Fhrvju2+hKH4i9j5pyDHmFzyK75cRe2WsIjra0CCXUxRqatn4Nd3IcO3l/xO8HYyJnzV4/KzB59JkQlcOnxjCTJ0D+KqpCV8wdNx6cNodLrqE+/19oNplsVJF78p7sYaGxqKQ2ovx3Xj+j8yBtAKWHh98SCRoLpdpLfUzHD0OfSSK4iVRPNSDqZeWaW00noRul0fjSeh2OfUTMvojDjcTJ4/fdESJF8UfflLGwzg6B78oHj4aP/5nX8SPLx/R8T//Mn58+YidPyHffkdIXz+1TnV2dnQMxM0fx70wLIonTlKrvbnF83GbEi9duJB8xeTYrlZC2tuUeMUv90RjPUOnz3pblfUTkplFQplZTUdo+fIYPX8aVOKptYInl1bwh4/S63DlZ6rDmXPR+MwsOR7POE57m6xkNH4Fj/Eh6Yx+b6vsj4+P6u/9pLODHl2tsfGHAhH9Lw62tHjo4RmS41NjsvtYEj6sOy49fJOT/oa33t53g8R+Zwr4CemXHgH07z8wkfAdKc7f15NEL9adkB69k6nyvF3C5e1v7m3u8dZ7AvTHixP13lr6A8b5+hrEW9OOuavmxdraFxKf/076u4gRgL3PqU6ADQaDnFAh6IQ0oSIYZKldHk0sW3lNeAbLNkEv2CN+V8QfjNg2YbfwezDo9bVR22ottwapFbjYgbbV6rQ57ULF2Qrhe8l2OsudTmt5h9VqjdjpOL7B7/P5AkH2uQSW+notNUl63WNoesWj6aUuNL3UhaaXutD0UheaXupC00tdaHqpC00vdaHppS40vdSFppe60PRSF2rTS/l/JSPmv5Zaelprjqkxgrwx1Bwpx+9cSfW/XY0ohuWGudxJ24Y0NFTPzC3/XzfN3Rtwu7/+g6b5W9oPvnewzBXZGTwPbPwW2NFQ96xNmbid9Gmzb7aZ2R0wTm6OK8w/7XuI3a+9+apUEKrLjDT/F+OYFUs='
